import React, { useMemo } from "react";
import styled from "styled-components";

import { useAppSelector } from "../redux/store";
import { tagSliceSelectors } from "../redux/tag/tag.selector";
import { Rounded } from "../theme/Rounded";
import { Spacing } from "../theme/Spacing";
import { Tooltip } from "../utils/local-antd";
import { Flex } from "./Flex";
import { TagChip } from "./TagChip";
import { FontSize } from "./Text";

const More = styled.span`
  padding: 0.1rem ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.darkerSub};
  font-size: ${FontSize.xs};
  font-weight: 600;
  cursor: default;
  flex-shrink: 0;
`;

type TagListProps = {
  tagIds?: string[];
  /** Chips shown before the rest collapse into "+N" - all of them when left out */
  max?: number;
  wrap?: boolean;
};

export const TagList: React.FC<TagListProps> = ({
  tagIds,
  max,
  wrap = false,
}) => {
  const tagsById = useAppSelector(tagSliceSelectors.selectTagsById);

  // Ids of tags deleted since the entry was loaded have nothing to show
  const tags = useMemo(
    () =>
      (tagIds || [])
        .map((id) => tagsById[id])
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tagIds, tagsById],
  );

  if (!tags.length) {
    return null;
  }

  const shown = max ? tags.slice(0, max) : tags;
  const hidden = tags.slice(shown.length);

  return (
    <Flex gap="xs" alignItems="center" flexWrap={wrap ? "wrap" : "nowrap"}>
      {shown.map((tag) => (
        <TagChip key={tag._id} tag={tag} />
      ))}

      {hidden.length ? (
        <Tooltip
          title={
            <Flex gap="xs" flexWrap="wrap">
              {hidden.map((tag) => (
                <TagChip key={tag._id} tag={tag} />
              ))}
            </Flex>
          }
        >
          <More>+{hidden.length}</More>
        </Tooltip>
      ) : null}
    </Flex>
  );
};
